import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IntegrationVendor } from '../models/integration/integration-vendor.enum';
import { IntegrationService } from '../services/integration.service';

/**
 * Vendor adapter pattern. Each connected vendor (D365 BC today, Abacus later,
 * Bexio at the per Company level if we ever fold it in) exposes a uniform
 * interface for the parts the auto matcher and the manage UI care about.
 *
 * The matcher itself stays vendor agnostic: it only sees `{id, name}` rows.
 * The adapter is the boundary that translates vendor specific fields
 * (BC's `displayName`, future Abacus's `mandantBezeichnung`, ...) into the
 * normalized shape.
 *
 * Adding a new vendor: write a new adapter + register it in `ADAPTERS`. No
 * changes to the matcher, the effect, or the manage UI.
 */
export interface IVendorAdapter {
    readonly vendor: IntegrationVendor;
    /**
     * Fetch the vendor's "external companies" for a given connection and
     * normalize them to `{id, name}`. The adapter decides which vendor field
     * is treated as the user facing name.
     */
    listExternalCompaniesNormalized(
        connectionId: string,
    ): Observable<Array<{ id: string; name: string }>>;
}

/**
 * D365 BC adapter. Reuses `IntegrationService.listExternalCompanies` and uses
 * the BC `displayName` as the matchable name (BC's `name` field is the API
 * code, `displayName` is what the customer sees in the BC UI).
 *
 * `||` (not `??`) on purpose: BC sometimes returns `displayName: ""` while
 * `name` holds the actual codename (classic Cronus demo data). With `??` the
 * empty string wins and the matcher sees blank names; with `||` the empty
 * string falls through to `name` and Cronus matches as expected. `trim()`
 * guards against pure-whitespace values doing the same trick.
 */
function buildD365BcAdapter(service: IntegrationService): IVendorAdapter {
    return {
        vendor: IntegrationVendor.D365_BC,
        listExternalCompaniesNormalized: (cnId) =>
            service.listExternalCompanies(cnId).pipe(
                map((cos) =>
                    cos.map((c) => ({
                        id: c.id,
                        name: c.displayName?.trim() || c.name?.trim() || '',
                    })),
                ),
            ),
    };
}

/**
 * Topal adapter. Reuses `IntegrationService.listExternalCompanies` (which proxies to
 * the BE's `TopalAuthAdapter.listExternalCompanies` → `GET /clients`). The BE already
 * normalizes the client `name`/`code` into `{id, name, displayName}` with `||` (empty
 * strings fall through), so the matchable name is `displayName || name`, same as BC.
 */
function buildTopalAdapter(service: IntegrationService): IVendorAdapter {
    return {
        vendor: IntegrationVendor.TOPAL,
        listExternalCompaniesNormalized: (cnId) =>
            service.listExternalCompanies(cnId).pipe(
                map((cos) =>
                    cos.map((c) => ({
                        id: c.id,
                        name: c.displayName?.trim() || c.name?.trim() || '',
                    })),
                ),
            ),
    };
}

/**
 * Accounto adapter. Reuses `IntegrationService.listExternalCompanies` (which proxies to
 * the BE's `AccountoAuthAdapter.listExternalCompanies`). A Treuhand key lists its client
 * tenants, a KMU key exactly its own tenant (REV-21119 D5); the BE maps each to
 * `{ id: String(tenant.id), name: tenant.name }`, so the matchable name is the tenant name.
 */
function buildAccountoAdapter(service: IntegrationService): IVendorAdapter {
    return {
        vendor: IntegrationVendor.ACCOUNTO,
        listExternalCompaniesNormalized: (cnId) =>
            service.listExternalCompanies(cnId).pipe(
                map((cos) =>
                    cos.map((c) => ({
                        id: c.id,
                        name: c.displayName?.trim() || c.name?.trim() || '',
                    })),
                ),
            ),
    };
}

/**
 * Proffix adapter. Reuses `IntegrationService.listExternalCompanies` (which proxies to
 * the BE's `ProffixAuthAdapter.listExternalCompanies` → `PRO/Datenbank`). The BE maps
 * each database to `{ id: Name, name: Name, displayName: Bezeichnung }`, so the matchable
 * name is the Bezeichnung (e.g. "Proffix Demodatenbank"), falling back to the DB name.
 */
function buildProffixAdapter(service: IntegrationService): IVendorAdapter {
    return {
        vendor: IntegrationVendor.PROFFIX,
        listExternalCompaniesNormalized: (cnId) =>
            service.listExternalCompanies(cnId).pipe(
                map((cos) =>
                    cos.map((c) => ({
                        id: c.id,
                        name: c.displayName?.trim() || c.name?.trim() || '',
                    })),
                ),
            ),
    };
}

/**
 * Lookup. Implemented as a factory call rather than a static `const` so the
 * adapters can hold a reference to the injected `IntegrationService`. Lives
 * here so the effect can call `getVendorAdapter(vendor)` without DI gymnastics.
 */
let cachedAdapters: Map<IntegrationVendor, IVendorAdapter> | null = null;

export function getVendorAdapter(
    vendor: IntegrationVendor,
    service: IntegrationService,
): IVendorAdapter {
    if (cachedAdapters == null) {
        cachedAdapters = new Map<IntegrationVendor, IVendorAdapter>([
            [IntegrationVendor.D365_BC, buildD365BcAdapter(service)],
            [IntegrationVendor.TOPAL, buildTopalAdapter(service)],
            [IntegrationVendor.ACCOUNTO, buildAccountoAdapter(service)],
            [IntegrationVendor.PROFFIX, buildProffixAdapter(service)],
        ]);
    }
    const adapter = cachedAdapters.get(vendor);
    if (adapter == null) {
        throw new Error(`No vendor adapter registered for ${vendor}`);
    }
    return adapter;
}
